const getNamedFormFields = (doc) => {
  if (!doc) return [];
  return Array.from(doc.querySelectorAll('input, textarea, select'))
    .filter((element) => !element.disabled && element.type !== 'hidden' && element.name);
};

export const collectMembershipFormData = (doc) => {
  const data = {};
  const processedRadioGroups = new Set();

  getNamedFormFields(doc).forEach((element) => {
    const name = element.name;
    const tagName = element.tagName.toLowerCase();
    const type = (element.type || '').toLowerCase();

    if (type === 'checkbox') {
      data[name] = element.checked ? 'true' : 'false';
      return;
    }

    if (type === 'radio') {
      if (processedRadioGroups.has(name)) return;
      processedRadioGroups.add(name);
      const group = doc.getElementsByName(name);
      const checked = Array.from(group).find((radio) => radio.checked);
      data[name] = checked ? String(checked.value || '') : '';
      return;
    }

    if (tagName === 'select') {
      data[name] = String(element.value || '');
      return;
    }

    data[name] = String(element.value || '').trim();
  });

  return data;
};

const isTextLikeInput = (type) => (
  type !== 'checkbox'
  && type !== 'radio'
  && type !== 'file'
  && type !== 'button'
  && type !== 'submit'
);

const setFieldValue = (element, rawValue) => {
  if (rawValue === undefined || rawValue === null) return;

  const tagName = element.tagName.toLowerCase();
  const type = (element.type || '').toLowerCase();
  const value = String(rawValue);

  if (type === 'checkbox') {
    element.checked = value === 'true' || value === '1' || value.toLowerCase() === 'yes';
    return;
  }

  if (type === 'radio') {
    element.checked = element.value === value;
    return;
  }

  if (tagName === 'select' || isTextLikeInput(type)) {
    element.value = value;
  }
};

export const populateMembershipForm = (doc, fields = {}) => {
  if (!doc || !fields || typeof fields !== 'object') return;

  getNamedFormFields(doc).forEach((element) => {
    if (Object.prototype.hasOwnProperty.call(fields, element.name)) {
      setFieldValue(element, fields[element.name]);
    }
  });
};

const MEMBERSHIP_META_HEADER_KEYS = new Set(['userTypeId', 'userTypeName', 'updatedAt', 'fields']);

export const getMembershipUserTypeId = (metaData) => {
  const id = metaData?.membership?.userTypeId;
  return id != null && String(id).trim() !== '' ? String(id).trim() : null;
};

export const resolveMembershipApplicationUserTypeId = (metaData, profileUserType) => {
  const fromMeta = getMembershipUserTypeId(metaData);
  if (fromMeta) return fromMeta;

  if (profileUserType == null) return null;
  const profileId = String(profileUserType).trim();
  return profileId || null;
};

export const extractStoredMembershipFormFields = (metaData) => {
  if (!metaData?.membership || typeof metaData.membership !== 'object') return {};

  if (metaData.membership.fields && typeof metaData.membership.fields === 'object') {
    return { ...metaData.membership.fields };
  }

  const fields = {};
  for (const [key, value] of Object.entries(metaData.membership)) {
    if (!MEMBERSHIP_META_HEADER_KEYS.has(key)) {
      fields[key] = value;
    }
  }
  return fields;
};

export const getMembershipFormFields = (metaData) => {
  // Field values are keyed by input name and can be reused across membership forms.
  // populateMembershipForm only applies values for fields present in the target form.
  return extractStoredMembershipFormFields(metaData);
};

export const buildMembershipMetaDataPayload = (fields, { userTypeId } = {}) => {
  const membership = {
    fields: fields || {}
  };

  if (userTypeId != null && String(userTypeId).trim() !== '') {
    membership.userTypeId = String(userTypeId).trim();
  }

  return { membership };
};

export const setMembershipFormReadOnly = (doc, readOnly = true) => {
  if (!doc) return;

  getNamedFormFields(doc).forEach((element) => {
    element.disabled = readOnly;
  });
};

export const humanizeMetaFieldKey = (key) => (
  String(key || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase())
);

export const userTypeHasValidMembership = (userType) => {
  const term = Number(userType?.membership?.term);
  return Number.isFinite(term) && term > 0;
};
